// localStorage-based persistence for comments. Cookies max out at 4kb per cookie which
// would only fit a handful of comments — localStorage gives us several MB.
//
// Schema is versioned in the key so future migrations can read old data.

import type { ClickOffsetPct, CommentStatus, ElementRect } from '@pinmerge/shared';

const STORAGE_KEY = 'pinmerge:comments:v1';

// Window event the widget dispatches when the host SPA navigates or the page-key
// override changes. main.ts patches history.pushState/replaceState + popstate to
// fan into this; useThreads listens and reloads the comment list.
export const LOCATION_CHANGE_EVENT = 'pinmerge:locationchange';

export interface LocalReaction {
  emoji: string;
  count: number;
  mine: boolean;
  /** Chronological reactor names (server-derived, capped at 50). Empty for
   *  comments not yet synced from the server. */
  reactor_names?: string[];
}

export interface LocalAvatar {
  /** path slug like 'people/49' served from <dashboard>/avatars/<slug>.svg */
  id: string | null;
  /** css color string used as circle background */
  bg: string | null;
}

export interface LocalComment {
  id: string;
  pageKey: string; // cleaned full URL — comments scope per page (see pageKey())
  authorName: string;
  text: string;
  /** anchor in document coordinates — fallback when the selector can't be
   *  resolved on the live page (legacy behaviour) */
  docX: number;
  docY: number;
  /** Click position as a fraction of elementRect. Used for hybrid pin
   *  positioning. Optional for back-compat with pre-0008 local entries. */
  clickOffsetPct?: ClickOffsetPct;
  elementSelector: string | null;
  elementHtml: string | null;
  elementRect: ElementRect | null;
  viewport: { w: number; h: number };
  createdAt: string;
  updatedAt: string;
  /** Cookie-bound author identity for local-only entries (the current browser's
   *  token). Legacy entries without a token are treated as "owned by this
   *  browser". Server-fetched entries no longer carry a creatorToken — the
   *  server sends `mine` instead (it never echoes other reviewers' tokens). */
  creatorToken?: string;
  /** Server-computed ownership flag for fetched entries. Undefined for
   *  local-only / legacy rows, where isOwnComment falls back to creatorToken. */
  mine?: boolean;
  /** UUID of the row in Supabase if synced; undefined if local-only or sync pending */
  remoteId?: string;
  /** Storage path of the uploaded screenshot, if any */
  screenshotPath?: string;
  /** Storage path of the uploaded element-area thumbnail, if any */
  thumbnailPath?: string;
  /** Signed URL for the thumbnail, returned by GET /api/comments. Not
   *  persisted across reloads — re-fetched on every list refresh. */
  thumbnailUrl?: string | null;
  /** Server-side triage status. Default 'open' for local-only entries. */
  status?: CommentStatus;
  /** Author's chosen avatar (null = fall back to initials circle). Server-side only. */
  avatar?: LocalAvatar;
  /** Aggregated reactions per emoji. Server-side only — local-only comments have []. */
  reactions?: LocalReaction[];
  /** Threading parent. Top-level comments have null. Replies have the id of
   *  their parent comment (a top-level comment id, since we only allow one
   *  level). For locally-created replies we store the *local* parent id; once
   *  the parent has a remoteId, the reply's API call must use the parent's
   *  remoteId — handled by mapping at sync time. */
  parentId?: string | null;
  /** Route + source anchor from an optional HostAdapter (WP-1.x). All optional —
   *  absent for entries captured without an adapter. camelCase here to match this
   *  file's convention; each maps 1:1 to a snake_case DB column:
   *  routePattern→route_pattern, routeParams→route_params, sourceFile→source_file,
   *  sourceLine→source_line, sourceColumn→source_column, componentName→component_name. */
  routePattern?: string;
  routeParams?: Record<string, string>;
  sourceFile?: string;
  sourceLine?: number;
  sourceColumn?: number;
  componentName?: string;
}

// Resolves the current page bucket from the live URL. Pure URL-keying: the
// bucket IS the URL (the truth), no mapping, no host configuration. We strip
// pinmerge-owned query params and the hash so they never split a bucket.
//
// MUST stay byte-identical to the server's pageKeyOf() in
// apps/dashboard/lib/widget-auth.ts — otherwise the stored key and the read-time
// filter won't match. Any change here is a change there.
export function pageKey(): string {
  return cleanUrlKey(window.location.href);
}

// origin + pathname + cleaned query string, hash discarded. "Cleaned" = every
// query param whose name starts with `pinmerge` is removed (prefix filter, so
// pinmerge / pinmerge_comment / pinmerge_auth / pinmerge_v / pinmerge_locale all
// drop out and a future pinmerge_* param stays future-proof). App-owned params
// are preserved verbatim, in their original order.
function cleanUrlKey(href: string): string {
  const u = new URL(href);
  const kept = new URLSearchParams();
  for (const [name, value] of u.searchParams) {
    if (!name.startsWith('pinmerge')) kept.append(name, value);
  }
  const qs = kept.toString();
  return u.origin + u.pathname + (qs ? `?${qs}` : '');
}

function readAll(): LocalComment[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as LocalComment[]) : [];
  } catch {
    return [];
  }
}

function writeAll(comments: LocalComment[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(comments));
  } catch (err) {
    console.error('[pinmerge] localStorage write failed', err);
  }
}

export function loadCommentsForPage(): LocalComment[] {
  const key = pageKey();
  return readAll()
    .filter((c) => c.pageKey === key)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function addLocalComment(comment: LocalComment): void {
  const all = readAll();
  all.push(comment);
  writeAll(all);
}

export function updateLocalComment(id: string, patch: Partial<LocalComment>): LocalComment | null {
  const all = readAll();
  const idx = all.findIndex((c) => c.id === id);
  if (idx === -1) return null;
  const current = all[idx];
  if (!current) return null;
  const updated: LocalComment = { ...current, ...patch, updatedAt: new Date().toISOString() };
  all[idx] = updated;
  writeAll(all);
  return updated;
}

export function deleteLocalComment(id: string): void {
  writeAll(readAll().filter((c) => c.id !== id));
}

export interface RemoteSnapshot {
  id: string; // server uuid
  authorName: string;
  text: string;
  docX: number;
  docY: number;
  clickOffsetPct: ClickOffsetPct | null;
  elementSelector: string | null;
  elementHtml: string | null;
  elementRect: ElementRect | null;
  viewport: { w: number; h: number };
  createdAt: string;
  updatedAt: string;
  mine: boolean;
  status: CommentStatus;
  screenshotPath: string | null;
  thumbnailPath: string | null;
  thumbnailUrl: string | null;
  avatar: LocalAvatar;
  reactions: LocalReaction[];
  parentId: string | null;
  /** Route + source anchor from an optional HostAdapter (WP-1.x). All optional —
   *  not yet emitted by the server. camelCase mirrors LocalComment; maps 1:1 to
   *  the snake_case DB columns. */
  routePattern?: string;
  routeParams?: Record<string, string>;
  sourceFile?: string;
  sourceLine?: number;
  sourceColumn?: number;
  componentName?: string;
}

// Merges a list of server-fetched comments into local storage for the current page.
//
// Rules:
// - For each remote: if local has a row with matching remoteId, refresh server-owned
//   fields (text, updatedAt, status) from server. If not, append a fresh local row.
// - Local rows with a remoteId that's no longer on the server are assumed deleted
//   (by us, elsewhere, or by the project owner via the dashboard) and removed locally.
// - Local-only rows (no remoteId) are left untouched — they're still pending sync.
//
// Returns the merged list for the current page so the caller can refresh state.
export function mergeRemoteComments(remote: RemoteSnapshot[]): LocalComment[] {
  const key = pageKey();
  const all = readAll();
  const otherPages = all.filter((c) => c.pageKey !== key);
  const localForPage = all.filter((c) => c.pageKey === key);
  const localByRemoteId = new Map<string, LocalComment>();
  for (const c of localForPage) {
    if (c.remoteId) localByRemoteId.set(c.remoteId, c);
  }
  const remoteIds = new Set(remote.map((r) => r.id));

  const merged: LocalComment[] = [];

  // Local-only (no remoteId) — keep as-is.
  for (const c of localForPage) {
    if (!c.remoteId) merged.push(c);
  }

  // Map remote parent ids to local row ids so locally-created replies can
  // resolve their parent reference via remoteId after merge.
  const localIdByRemoteId = new Map<string, string>();
  for (const c of localForPage) {
    if (c.remoteId) localIdByRemoteId.set(c.remoteId, c.id);
  }
  for (const r of remote) {
    const existing = localByRemoteId.get(r.id);
    if (!existing) {
      // We'll mint a new local id below; pre-record it so children find it.
      const newLocalId = crypto.randomUUID();
      localIdByRemoteId.set(r.id, newLocalId);
      // Stash newLocalId on the remote row temporarily so the loop below uses it.
      (r as RemoteSnapshot & { __localId?: string }).__localId = newLocalId;
    }
  }

  // Remote rows — update existing local copy or create new.
  for (const r of remote) {
    const existing = localByRemoteId.get(r.id);
    const localParentId = r.parentId ? localIdByRemoteId.get(r.parentId) ?? null : null;
    if (existing) {
      merged.push({
        ...existing,
        authorName: r.authorName,
        text: r.text,
        updatedAt: r.updatedAt,
        mine: r.mine,
        status: r.status,
        screenshotPath: r.screenshotPath ?? existing.screenshotPath,
        thumbnailPath: r.thumbnailPath ?? existing.thumbnailPath,
        thumbnailUrl: r.thumbnailUrl,
        clickOffsetPct: r.clickOffsetPct ?? existing.clickOffsetPct,
        elementRect: r.elementRect ?? existing.elementRect,
        elementSelector: r.elementSelector ?? existing.elementSelector,
        avatar: r.avatar,
        reactions: r.reactions,
        parentId: localParentId,
      });
    } else {
      const newLocalId = (r as RemoteSnapshot & { __localId?: string }).__localId ?? crypto.randomUUID();
      merged.push({
        id: newLocalId,
        pageKey: key,
        authorName: r.authorName,
        text: r.text,
        docX: r.docX,
        docY: r.docY,
        elementSelector: r.elementSelector,
        elementHtml: r.elementHtml,
        elementRect: r.elementRect,
        viewport: r.viewport,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
        remoteId: r.id,
        mine: r.mine,
        status: r.status,
        avatar: r.avatar,
        reactions: r.reactions,
        thumbnailUrl: r.thumbnailUrl,
        parentId: localParentId,
        ...(r.clickOffsetPct ? { clickOffsetPct: r.clickOffsetPct } : {}),
        ...(r.screenshotPath ? { screenshotPath: r.screenshotPath } : {}),
        ...(r.thumbnailPath ? { thumbnailPath: r.thumbnailPath } : {}),
      });
    }
  }

  // Synced rows whose server-side row has vanished — drop them.
  // (Already handled implicitly: they aren't in `merged` because we only added rows
  // present in `remote`. But we still want to keep server-deleted handling explicit
  // for the no-op case below.)
  void remoteIds;

  merged.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  writeAll([...otherPages, ...merged]);
  return merged;
}
